import { NgModule } from '@angular/core'
import { FormlyModule, FormlyFieldConfig } from '@ngx-formly/core'
import { FormlyMaterialModule } from '@ngx-formly/material'
import { ADDRESS_AUTOCOMPLETE, ADDRESS_COUNTRY_AUTOCOMPLETE, CA_PLAIN_ADDRESS, CA_PLAIN_ADDRESS_COUNTRY, COUNTRY_OPTIONS, PLAIN_ADDRESS, STATE_PROVINCE_LIST } from 'src/data/address.form'
import { FORM_TO_COMBO, buildDates } from 'src/data/form'
import { POBoxboxValidator, decimalValidator, ineligibleValidator, acknowledgedValidator, futureValidator, validDate, minDateValidator, maxDateValidator } from 'src/data/validators'
import { EMPLOYMENT_HISTORY_ENTRY } from 'src/data/employment.form'
import { VN_ADDRESS } from 'src/data/VN-address'
import { IN_ADDRESS } from 'src/data/IN-address'
import { CN_CITIES, CN_CITIES_BY_PROVINCE, CN_DISTRICTS, CN_PROVINCES } from 'src/data/CN-address'
import { StepperComponent } from './containers/order/traveler/form/stepper/stepper.component'
import { SocialSecurityComponent } from './containers/order/traveler/form/social.security/social.security.component'
import { SecureScanComponent } from './containers/order/traveler/form/secure.scan/secure.scan.component'
import { MultipleComponent } from './containers/order/traveler/form/multiple/multiple.component'
import { MultiSelectComponent } from './containers/order/traveler/form/multi-select/multi-select.component'
import { MaskedInputComponent } from './containers/order/traveler/form/masked.input/masked.input.component'
import { GoogleAddressComponent } from './containers/order/traveler/form/google.address/google.address.component'
import { FacilityFinderComponent } from './containers/order/traveler/form/facility.finder/facility.finder.component'
import { DatepickerComponent } from './containers/order/traveler/form/datepicker/datepicker.component'
import { CheckboxComponent } from './containers/order/traveler/form/checkbox/checkbox.component'
import { ButtonSelectComponent } from './containers/order/traveler/form/button.select/button.select.component'
import { RadioButtonComponent } from './containers/order/traveler/form/radio.button/radio.button.component'
import { AutocompleteComponent } from './containers/order/traveler/form/autocomplete/autocomplete.component'
import { ChoiceSectionsComponent } from './containers/order/traveler/form/choice.sections/choice.sections.component'
import { PaymentModuleComponent } from './containers/order/traveler/form/payment.module/payment.module.component'

/** Resolves the form control a mapped field depends on: props.parentKey, relative to the field unless props.parent === 'global'. */
const resolveMappedParent = (field: FormlyFieldConfig): any => {
	let parentKey = field.props?.['parentKey']
	if (!parentKey) return null

	let root: FormlyFieldConfig = field.parent as FormlyFieldConfig
	if (field.props?.['parent'] === 'global') {
		while (root && root.parent) {
			root = root.parent
		}
	} else {
		const segments = (field.key as string).split('.')
		segments.pop()
		segments.push(parentKey)
		parentKey = segments.join('.')
	}

	let control = root?.formControl as any
	for (const segment of parentKey.split('.')) {
		control = control?.controls?.[segment]
	}

	return control || null
}

@NgModule({
	imports: [
		FormlyMaterialModule,
		StepperComponent,
		SocialSecurityComponent,
		SecureScanComponent,
		MultipleComponent,
		MultiSelectComponent,
		MaskedInputComponent,
		GoogleAddressComponent,
		FacilityFinderComponent,
		DatepickerComponent,
		CheckboxComponent,
		ButtonSelectComponent,
		RadioButtonComponent,
		AutocompleteComponent,
		ChoiceSectionsComponent,
		PaymentModuleComponent,
		FormlyModule.forRoot({
			types: [
				{
					name: 'name-field', 
					extends: 'input',
					defaultOptions: {
						props: {
							pattern: "^[A-Za-z]+(?:[ \\-]?[A-Za-z]+)*$",
							maxLength: 35
						},
						validation: {
							messages: {
								pattern: "Please use only letters, spaces, dashes and apostrophes. No contiguous dashes/apostrophes."
							}
						}
					}
				},
				{ name: 'buttons', component: ButtonSelectComponent },
				{ name: 'radio-button', component: RadioButtonComponent },
				{ name: 'choice-sections', component: ChoiceSectionsComponent },
				{
					name: 'yes-no-buttons',
					extends: 'buttons',
					defaultOptions: {
						props: {
							required: true,
							options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]
						}
					}
				},
				{ name: 'datepicker', 
					component: DatepickerComponent,
					defaultOptions: {
						expressions: {
							'props.minDate': (field: FormlyFieldConfig) => {
								return buildDates(field, 'min')
							},
							'props.maxDate': (field: FormlyFieldConfig) => {
								return buildDates(field, 'max')
							}
						},
						validators: {
							validation: ['valid-date', 'min-date', 'max-date']
						}
					}
				},
				{ name: 'multiple', component: MultipleComponent },
				{ name: 'masked-input', component: MaskedInputComponent },
				{ name: 'stepper', component: StepperComponent },
				{ name: 'social-security-input', component: SocialSecurityComponent },
				{ name: 'location-finder', component: FacilityFinderComponent },
				{ name: 'multi-select', component: MultiSelectComponent },
				{ name: 'autocomplete', component: AutocompleteComponent },
				{ name: 'google-address', component: GoogleAddressComponent },
				{
					name: 'country-autocomplete',
					extends: 'autocomplete',
					defaultOptions: {
						props: {
							required: true,
							label: 'Country',
							options: COUNTRY_OPTIONS
						}
					}
				},
				{ name: 'secure-scanner', component: SecureScanComponent },
				{ name: 'gwa-checkbox', component: CheckboxComponent },
				{ name: 'payment-module', component: PaymentModuleComponent },
				{
					name: 'mapped-autocomplete',
					extends: 'autocomplete',
					defaultOptions: {
						hooks: {
							onInit: (field: FormlyFieldConfig) => {
								const parent = resolveMappedParent(field)
								if (!parent) {
									field.hide = true
									return
								}

								const setOptions = (value: string) => {
									const options = field.props?.['mappedOptions']?.[value]
									field.hide = !options
									if (options) {
										field.props = { ...field.props, options }
									}
								}

								setOptions(parent.value as string)
								parent.valueChanges.subscribe((value: string) => {
									field.formControl?.patchValue('')
									field.formControl?.markAsUntouched()
									setOptions(value)
								})
							}
						}
					}
				},
				{
					// Free-text stand-in for a mapped-autocomplete: shows only when the parent has a value with no mapping yet.
					name: 'mapped-fallback-input',
					extends: 'input',
					defaultOptions: {
						hooks: {
							onInit: (field: FormlyFieldConfig) => {
								const parent = resolveMappedParent(field)
								if (!parent) {
									field.hide = true
									return
								}

								const setVisibility = (value: string) => {
									field.hide = !value || !!field.props?.['mappedOptions']?.[value]
								}

								setVisibility(parent.value as string)
								parent.valueChanges.subscribe((value: string) => {
									field.formControl?.patchValue('')
									field.formControl?.markAsUntouched()
									setVisibility(value)
								})
							}
						}
					}
				},
				{
					name: 'cn-province',
					extends: 'autocomplete',
					defaultOptions: {
						props: {
							label: 'Province/State',
							options: CN_PROVINCES
						}
					}
				},
				{
					name: 'cn-city',
					extends: 'autocomplete',
					defaultOptions: {
						props: {
							label: 'City',
							options: CN_CITIES
						}
					}
				},
				{
					name: 'cn-city-by-province',
					extends: 'mapped-autocomplete',
					defaultOptions: {
						props: {
							label: 'City',
							mappedOptions: CN_CITIES_BY_PROVINCE,
							parentKey: 'province'
						}
					}
				},
				{
					name: 'cn-district',
					extends: 'mapped-autocomplete',
					defaultOptions: {
						props: {
							label: 'District/County',
							mappedOptions: CN_DISTRICTS,
							parentKey: 'city'
						}
					}
				},
				{
					name: 'cn-district-manual',
					extends: 'mapped-fallback-input',
					defaultOptions: {
						props: {
							label: 'District/County',
							mappedOptions: CN_DISTRICTS,
							parentKey: 'city',
							maxLength: 50
						}
					}
				},
				{
					name: 'vn-province',
					extends: 'autocomplete',
					defaultOptions: {
						props: {
							required: true,
							label: 'City/Province',
							options: Object.keys(VN_ADDRESS).map((key) => ({
								label: key,
								value: key
							}))
						}
					}
				},
				{
					name: 'vn-ward',
					extends: 'mapped-select',
					defaultOptions: {
						props: {
							mappedOptions: VN_ADDRESS,
							parentKey: 'city',
							label: 'Ward/Commune',
							required: true
						}
					}
				},
				{
					name: 'in-state',
					extends: 'autocomplete',
					defaultOptions: {
						props: {
							required: true,
							label: "State",
							options: [
								{ label: "ANDAMAN AND NICOBAR ISLANDS", value: "ANDAMAN AND NICOBAR ISLANDS" },
								{ label: "ANDHRA PRADESH", value: "ANDHRA PRADESH" },
								{ label: "ARUNACHAL PRADESH", value: "ARUNACHAL PRADESH" },
								{ label: "ASSAM", value: "ASSAM" },
								{ label: "BIHAR", value: "BIHAR" },
								{ label: "CHANDIGARH", value: "CHANDIGARH" },
								{ label: "CHHATTISGARH", value: "CHHATTISGARH" },
								{ label: "DADRA NAGAR HAVELI AND DAMAN AND DIU", value: "DADRA NAGAR HAVELI AND DAMAN AND DIU" },
								{ label: "DELHI", value: "DELHI" },
								{ label: "GOA", value: "GOA" },
								{ label: "GUJARAT", value: "GUJARAT" },
								{ label: "HARYANA", value: "HARYANA" },
								{ label: "HIMACHAL PRADESH", value: "HIMACHAL PRADESH" },
								{ label: "JAMMU AND KASHMIR", value: "JAMMU AND KASHMIR" },
								{ label: "JHARKHAND", value: "JHARKHAND" },
								{ label: "KARNATAKA", value: "KARNATAKA" },
								{ label: "KERALA", value: "KERALA" },
								{ label: "LADAKH", value: "LADAKH" },
								{ label: "MADHYA PRADESH", value: "MADHYA PRADESH" },
								{ label: "MAHARASHTRA", value: "MAHARASHTRA" },
								{ label: "MANIPUR", value: "MANIPUR" },
								{ label: "MEGHALAYA", value: "MEGHALAYA" },
								{ label: "MIZORAM", value: "MIZORAM" },
								{ label: "NAGALAND", value: "NAGALAND" },
								{ label: "ORISSA", value: "ORISSA" },
								{ label: "PONDICHERRY", value: "PONDICHERRY" },
								{ label: "PUNJAB", value: "PUNJAB" },
								{ label: "RAJASTHAN", value: "RAJASTHAN" },
								{ label: "SIKKIM", value: "SIKKIM" },
								{ label: "TAMIL NADU", value: "TAMIL NADU" },
								{ label: "TELANGANA", value: "TELANGANA" },
								{ label: "TRIPURA", value: "TRIPURA" },
								{ label: "UTTAR PRADESH", value: "UTTAR PRADESH" },
								{ label: "WEST BENGAL", value: "WEST BENGAL" },
								{ label: "LAKSHADWEEP", value: "LAKSHADWEEP" },
								{ label: "UTTARAKHAND", value: "UTTARAKHAND" }
							]
						}
					}
				},
				{
					name: 'in-district',
					extends: 'mapped-select',
					defaultOptions: {
						props: {
							mappedOptions: IN_ADDRESS,
							parentKey: 'state',
							label: 'District',
							required: true
						}
					}
				},
				{
					name: 'mapped-select',
					extends: 'select',
					defaultOptions: {
						expressions: {
							hide: (field: FormlyFieldConfig) => {
								const parentKey = field.props?.['parentKey']
								if (!parentKey) return true
								
								const parentDetailKey = field.props?.['parentDetailKey']
								let root: FormlyFieldConfig = field.parent as FormlyFieldConfig
								
								if (field.props?.['parent'] === 'global') {
									while (root && root.parent) {
										root = root.parent
									}
								}
								
								const pathSegments = parentKey.split('.')
								let parentControl = root?.formControl as any
								
								if (!parentControl) return true
								
								for (const segment of pathSegments) {
									parentControl = parentControl?.controls?.[segment]

									if (!parentControl) {
										let segments = (field.key as string).split('.')
										segments.pop()
										segments.push(field.props?.['parentKey'])
										const newParentKey = segments.join('.')

										const pathSegments = newParentKey.split('.')
										parentControl = root?.formControl as any
		
										for (const segment of pathSegments) {
											parentControl = parentControl?.controls?.[segment] as any
										}
									}

									if (!parentControl) return true
								}
								
								const parentValue = parentControl.value
								if (!parentValue) return true
								
								if (!parentDetailKey) {
									return !(field.props?.['mappedOptions']?.[parentValue])
								} else {
									const parentDetailControl = (field.parent?.formControl as any)?.controls?.[parentDetailKey]
									const parentDetailValue = parentDetailControl?.value
									if (!parentDetailValue) return true
									return !(field.props?.['mappedOptions']?.[parentValue]?.[parentDetailValue])
								}
							}
						},
						hooks: {
							onChanges: (field: FormlyFieldConfig) => {
								console.log('field: ', field)
								const parentKey = field.props?.['parentKey']
								const parentDetailKey = field.props?.['parentDetailKey']

								if (parentKey) {
									let root: FormlyFieldConfig = field.parent as FormlyFieldConfig
	
									if (field.props?.['parent'] === 'global') {
										while (root && root.parent) {
											root = root.parent
										}
									}

									const setFieldExpressionValues = (parentValue: string, parentDetailValue: string|null) => {
										if (!parentValue) return

										
										const mappedOptionsResult = parentDetailValue 
											? field.props?.['mappedOptions']?.[parentValue]?.[parentDetailValue] || [] 
											: field.props?.['mappedOptions']?.[parentValue] || []
										
										let newOptions: any[] = []

										if (Array.isArray(mappedOptionsResult)) {
											if (typeof mappedOptionsResult[0] === 'string') {
												newOptions = mappedOptionsResult.map((option: string) => ({
													label: option,
													value: option
												}))
											} else {
												newOptions = mappedOptionsResult
											}
										} else if (typeof mappedOptionsResult === 'object') {
											newOptions = Object.keys(mappedOptionsResult).map((key) => ({
												label: key,
												value: key
											}))
										}

										if (field.props?.['enableNone']) {
											newOptions.unshift({
												label: 'None',
												value: ''
											})
										}

										field.props = {
											...field.props,
											options: newOptions
										}
									}

									const pathSegments = parentKey.split('.')
									let parentControl = root?.formControl as any
	
									for (const segment of pathSegments) {
										parentControl = parentControl?.controls?.[segment]
									}
									
									if (!parentControl) {
										let segments = (field.key as string).split('.')
										segments.pop()
										segments.push(field.props?.['parentKey'])
										const newParentKey = segments.join('.')

										const pathSegments = newParentKey.split('.')
										parentControl = root?.formControl as any
		
										for (const segment of pathSegments) {
											parentControl = parentControl?.controls?.[segment] as any
										}
									}

									if (!parentControl) return
									
									let parentDetailControl = parentDetailKey ? (field.parent?.formControl as any)?.controls?.[parentDetailKey] : null

									setFieldExpressionValues(parentControl.value as string, parentDetailControl?.value || null)

									parentControl.valueChanges.subscribe((value: string) => {
										if (!parentDetailKey) {
											field.formControl?.patchValue('')
											field.formControl?.markAsUntouched()

											setFieldExpressionValues(value, null)
										} else {
											if (!parentDetailControl) {
												parentDetailControl = (field.parent?.formControl as any)?.controls?.[field.props?.['parentDetailKey']]

												if (parentDetailControl) {
													parentDetailControl.valueChanges.subscribe((detailValue: string) => {
														field.formControl?.patchValue('')
														field.formControl?.markAsUntouched()

														setFieldExpressionValues(value, detailValue)
													})
												}
											}
										}
									})  
								} else {
									field.hide = true
								}
							}
						}
					}
				},
				{
					name: 'state-province-autocomplete',
					extends: 'autocomplete',
					defaultOptions: {
						props: {
							mappedOptions: STATE_PROVINCE_LIST,
						},
						hooks: {
							onInit: (field: FormlyFieldConfig) => {
								const setFieldExpressionValues = (parentValue: string) => {
									field.hide = !(field.props?.['mappedOptions'][parentValue])
									if (!field.hide) {
										field.props = {
											...field.props,
											options: field.props?.['mappedOptions'][parentValue] || [],
											label: parentValue === 'US' ? 'State' : 'Province'
										}
									}
								}

								let parentKey = field.props?.['parentKey']
								if (parentKey) {
									let root: FormlyFieldConfig = field.parent as FormlyFieldConfig
	
									if (field.props?.['parent'] === 'global') {
										while (root && root.parent) {
											root = root.parent
										}
									} else {
										let segments = (field.key as string).split('.')
										segments.pop()
										segments.push(field.props?.['parentKey'])
										parentKey = segments.join('.')
									}
									
									const pathSegments = parentKey.split('.')
									let parentControl = root?.formControl as any
									
									for (const segment of pathSegments) {
										parentControl = parentControl.controls[segment]
									}

									setFieldExpressionValues(parentControl.value as string)

									parentControl.valueChanges.subscribe((value: string) => {
										field.formControl?.patchValue('')
										field.formControl?.markAsUntouched()

										setFieldExpressionValues(value)
									})
								} else {
									const parentValue = field.props?.['parentValue']
									if (parentValue) {
										setFieldExpressionValues(parentValue)
									}
								}
							}
						}
					}
				},
				{
					name: 'address-autocomplete',
					extends: 'formly-group',
					defaultOptions: {
						fieldGroup: ADDRESS_AUTOCOMPLETE,
						expressions: {
							'hide': (field: FormlyFieldConfig) => {
								const country = field.model?.country || field.props?.['country']
								return country ? false : true
							}
						}
					},
				},
				{
					name:'address-country-autocomplete',
					extends: 'formly-group',
					defaultOptions: {
						fieldGroup: ADDRESS_COUNTRY_AUTOCOMPLETE
					}
				},
				{
					name:'plain-address',
					extends: 'formly-group',
					defaultOptions: {
						fieldGroup: PLAIN_ADDRESS
					}
				},
				{
					name:'ca-plain-address',
					extends: 'formly-group',
					defaultOptions: {
						fieldGroup: CA_PLAIN_ADDRESS
					}
				},
				{
					name:'ca-plain-address-country',
					extends: 'formly-group',
					defaultOptions: {
						fieldGroup: CA_PLAIN_ADDRESS_COUNTRY
					}
				},
				{
					name: 'employment-history',
					extends: 'formly-group',
					defaultOptions: {

					}
				},
				{
					name: 'from-to',
					extends: 'formly-group',
					defaultOptions: {
						fieldGroup: FORM_TO_COMBO,
						fieldGroupClassName: "gwc-form__field-group"
					}
				},
				{
					name: 'employment-history-entry',
					extends: 'formly-group',
					defaultOptions: {
						fieldGroup: EMPLOYMENT_HISTORY_ENTRY,
						fieldGroupClassName: "gwc-form__field-group"
					}
				},
			],
			validationMessages: [
				{ name: 'required', message: 'This field is required.' },
				{ name: 'decimal', message: 'You must enter a round number.' },
				{ name: 'pobox', message: 'P.O. Box addresses cannot be used on this form.' },
				{ name: 'ineligible', message: 'This answer makes you ineligible for this application.' },
				{ name: 'acknowledged', message: 'Please check this box to continue.' },
				{ name: 'future', message: (error: any, field: FormlyFieldConfig) => {
					return `${field.props?.label} cannot be in the future.`
				}},
				{ name: 'min-date', message: (error: any, field: FormlyFieldConfig) => {
					const msg = field.validation?.messages?.['min']
					return typeof msg === 'string' ? msg : 'Date is too early.'
				}},
				{ name: 'max-date', message: (error: any, field: FormlyFieldConfig) => {
					const msg = field.validation?.messages?.['max']
					return typeof msg === 'string' ? msg : 'Date is too late.'
				}}
			],
			validators: [
				{ name: 'decimal', validation: decimalValidator },
				{ name: 'pobox', validation: POBoxboxValidator },
				{ name: 'ineligible', validation: ineligibleValidator },
				{ name: 'acknowledged', validation: acknowledgedValidator },
				{ name: 'valid-date', validation: validDate},
				{ name: 'future', validation: futureValidator},
				{ name: 'min-date', validation: minDateValidator },
				{ name: 'max-date', validation: maxDateValidator }
			],
			extras: {
				checkExpressionOn: 'modelChange',
				resetFieldOnHide: false
			},
		})
	],
	exports: [FormlyModule, FormlyMaterialModule]
})
export class FormlyConfigModule { }

